import { Alert, Snackbar } from '@mui/material';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { foutmelding } from '../api/fouten.ts';

export type Meldingsoort = 'success' | 'error' | 'info' | 'warning';

interface Melding {
  tekst: string;
  soort: Meldingsoort;
}

interface Melder {
  /**
   * Toont kort een melding onderin het scherm.
   * @param tekst De melding.
   * @param soort De soort, standaard een bevestiging.
   */
  meld(tekst: string, soort?: Meldingsoort): void;

  /**
   * Toont de melding bij een fout.
   * @param fout De opgevangen fout.
   */
  meldFout(fout: unknown): void;
}

const MeldingenContext = createContext<Melder>({ meld: () => undefined, meldFout: () => undefined });

/**
 * Toont meldingen van de app, zoals "Kandidaat opgeslagen" of een foutmelding.
 * @param props De inhoud van de app.
 * @returns De provider met één snackbar.
 */
export function MeldingenProvider({ children }: { children: ReactNode }) {
  const [melding, setMelding] = useState<Melding | null>(null);
  const [open, setOpen] = useState(false);
  const meld = useCallback((tekst: string, soort: Meldingsoort = 'success') => {
    setMelding({ tekst, soort });
    setOpen(true);
  }, []);
  const meldFout = useCallback((fout: unknown) => meld(foutmelding(fout), 'error'), [meld]);
  const waarde = useMemo(() => ({ meld, meldFout }), [meld, meldFout]);

  return (
    <MeldingenContext.Provider value={waarde}>
      {children}
      {melding && (
        <Snackbar
          open={open}
          autoHideDuration={melding.soort === 'error' ? 8000 : 4000}
          onClose={(_gebeurtenis, reden) => {
            if (reden !== 'clickaway') {
              setOpen(false);
            }
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity={melding.soort} variant="filled" onClose={() => setOpen(false)} sx={{ width: '100%' }}>
            {melding.tekst}
          </Alert>
        </Snackbar>
      )}
    </MeldingenContext.Provider>
  );
}

/**
 * Geeft de functies om meldingen te tonen.
 * @returns `meld` en `meldFout`.
 */
export function useMeldingen(): Melder {
  return useContext(MeldingenContext);
}
